import React from 'react';
import { AlertTriangle, Search, Bed, Bug, Moon, CheckCircle2 } from 'lucide-react';
import { companyInfo } from '../data/mock';

const BedBugSpecialistSection = () => {
  const signs = [
    {
      title: "Blood Spots on Bedding",
      description: "Small reddish-brown stains on sheets, pillowcases, or mattresses from crushed bugs.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/lhvr1598_Screenshot%202026-01-19%20at%2018.33.06.png"
    },
    {
      title: "Itchy Bite Marks",
      description: "Red, itchy welts often appearing in lines or clusters, typically on exposed skin while sleeping.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/zsityk6r_Screenshot%202026-01-19%20at%2018.01.45.png"
    },
    {
      title: "Dark Faecal Spots",
      description: "Tiny dark spots (bed bug excrement) on mattress seams, bed frames, and nearby furniture.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/evua8i7j_Screenshot%202026-01-19%20at%2017.57.27.png"
    },
    {
      title: "Shed Skins & Eggs",
      description: "Pale yellow skins shed by nymphs as they grow, plus tiny white eggs in crevices.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/8rmm189z_Screenshot%202026-01-19%20at%2018.34.15.png"
    },
    {
      title: "Severe Infestations",
      description: "Heavy infestations show clusters of bugs, eggs, and debris in mattress seams and crevices.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/e49fwisg_Screenshot%202026-01-19%20at%2017.59.01.png"
    },
    {
      title: "Live Bugs",
      description: "Adult bed bugs are oval, flat, reddish-brown, about 5mm long - often hiding in seams and cracks.",
      image: "https://customer-assets.emergentagent.com/job_8a7f2b42-1e84-4152-94e9-4de05be1797d/artifacts/q8xuwxfn_Screenshot%202026-01-19%20at%2018.35.56.png"
    }
  ];

  const problems = [
    "A single female bed bug can lay between 1 and 10 eggs per day, and up to 200-500 eggs in her lifetime",
    "They hide in mattresses, bed frames, furniture, curtains, skirting boards, and electrical outlets",
    "Bites cause itchy red welts, allergic reactions, and sleep disruption",
    "DIY treatments often fail as bed bugs are resistant to many over-the-counter products",
    "Infestations cause significant stress, anxiety, and embarrassment",
    "Without professional treatment, the problem will only get worse over time"
  ];

  return (
    <section className="py-24 bg-neutral-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand/5 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-brand/10 border border-brand/20 rounded-full px-4 py-2 mb-6">
            <Bug className="w-5 h-5 text-brand" />
            <span className="text-brand font-semibold">Lincolnshire's Bed Bug Experts</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
            Bed Bug Specialists with<br />
            <span className="text-brand">{companyInfo.experience} Years Experience</span>
          </h2>
          <p className="text-neutral-600 max-w-3xl mx-auto text-lg">
            Bed bugs can turn your peaceful nights into a nightmare. At {companyInfo.fullName}, 
            we specialise in providing reliable, safe, and effective bed bug control services, 
            helping you reclaim your home and enjoy a bed bug-free environment.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Signs of Infestation */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-brand rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-neutral-900">Signs of Bed Bug Infestation</h3>
            </div>
            
            <div className="space-y-4">
              {signs.map((sign, index) => (
                <div 
                  key={index}
                  className="bg-white border border-neutral-200 rounded-xl p-4 hover:border-brand/30 hover:shadow-lg transition-all duration-300 flex gap-4 items-center"
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-neutral-100">
                    <img 
                      src={sign.image} 
                      alt={sign.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-neutral-900 font-semibold mb-1">{sign.title}</h4>
                    <p className="text-neutral-600 text-sm">{sign.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why Professional Treatment */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-brand rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-neutral-900">Why Professional Treatment Matters</h3>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl p-6 mb-6">
              <p className="text-neutral-700 leading-relaxed mb-6">
                In 2026, bed bug infestations remain a critical issue due to their adaptability, but professional treatment methods have become more sophisticated to counter chemical resistance.
              </p>
              
              <ul className="space-y-3">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-brand rounded-full mt-2 flex-shrink-0" />
                    <span className="text-neutral-600 text-sm">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Bed Bug Life Cycle - moved here */}
            <div className="bg-white border border-brand/30 rounded-2xl p-6">
              <h4 className="text-neutral-900 font-semibold mb-4 text-center text-lg">Understanding the Bed Bug Life Cycle</h4>
              <div className="rounded-xl overflow-hidden max-w-md mx-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bedbug-experts/artifacts/47wjpyqp_Screenshot%202026-01-20%20at%2013.45.42.png"
                  alt="Bed Bug Life Cycle - from egg to adult"
                  className="w-full h-auto"
                />
              </div>
              <p className="text-neutral-600 text-sm text-center mt-4">
                Bed bugs go through 5 nymph stages before becoming adults. Professional treatment targets every stage of the life cycle for the most effective results.
              </p>
            </div>
          </div>
        </div>

        {/* Bedroom Image - Centered below both columns */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="relative rounded-2xl overflow-hidden h-72">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/63feb3c8-d310-4f34-a719-3c3caba9655a/images/3af008d54af38f3f4d58d2c59a6b62475d9a1ba066af6292dbc90eaca50371ec.png"
              alt="Professional bed bug treatment in a hotel bedroom with steam cleaner and vacuum"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-center">
              <div className="flex items-center justify-center gap-2">
                <Moon className="w-6 h-6 text-brand" />
                <span className="text-white font-semibold text-xl">Reclaim Your Peaceful Nights Sleep</span>
              </div>
            </div>
          </div>
        </div>

        {/* What We Provide */}
        <div className="bg-brand/10 border border-brand/30 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4">
              Our Specialist Bed Bug Service Includes
            </h3>
            <p className="text-neutral-600">
              Comprehensive treatment from inspection to prevention
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Search,
                title: "Thorough Inspection",
                description: "Complete property survey including bed dismantling, furniture, flooring, curtains, and hard-to-reach crevices"
              },
              {
                icon: Bed,
                title: "Tailored Treatment",
                description: "Customised plans based on severity, with chemical and chemical-free options available"
              },
              {
                icon: Bug,
                title: "Full Life-Cycle Treatment",
                description: "Advanced techniques targeting every stage — eggs, nymphs and adults — for the most thorough treatment possible"
              },
              {
                icon: CheckCircle2,
                title: "Aftercare & Prevention",
                description: "Ongoing monitoring advice and practical guidance to prevent future infestations"
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-brand rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-neutral-900 font-semibold mb-2">{item.title}</h4>
                <p className="text-neutral-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-brand font-semibold mb-2">Free Consultations & Fast Response Times</p>
            <p className="text-neutral-600 text-sm">
              We understand this is a sensitive issue and treat every case with discretion and care.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BedBugSpecialistSection;
